/**
 * The shared live-pill row shell (#967 review) — the layout + batching +
 * per-pill mechanics that `LiveHomePill` (my-spaces rail) and
 * `LiveMeetversePills` (meetverse rail) duplicated verbatim. Callers keep
 * ONLY their differences: which entries (selection/eligibility/legacy
 * filtering live in their data hooks and rows), which analytics surface, and
 * — for a caller that merges more than one source into this ONE row
 * (`LiveMeetversePills`, personal + public, Dexter's one-row ruling
 * 2026-07-27) — a `surfaceFor` override so entries from different sources
 * keep firing their own distinct surface.
 *
 * What lives here, once:
 *   - the `w-full max-w-2xl mx-auto px-4 mt-2` wrapper that mirrors the feed
 *     container width (and collapses to nothing when the row is empty — no
 *     phantom gap);
 *   - the `SwipeRow` (§0 width rule: multi = 90% snap slots with the next
 *     peeking, single = full width with zero scroll chrome) with its
 *     `{eventId}:{phase}` depKey and the shared `useLiveFrontResetKey`
 *     scroll-start token — post-mount, the row's horizontal scroll resets only
 *     on an observed not-live→live transition at the front (see that hook);
 *   - ONE `useEventFeedCounts` batch per row (T-8): every LIVE pill's id at
 *     once — soon pills batch nothing (posts are live-only), so their per-id
 *     pending reads false by construction and no skeleton shows. Counts are
 *     universal (#970): guests fetch the same numbers as members, so the
 *     public meetverse row shows the posts tail signed-out too — this shell
 *     adds no auth of its own;
 *   - the pill item: composers + `LivePill` props, the once-per-APPEARANCE
 *     impression (event+`surface:phase` via `useLiveImpression` — a phase
 *     flip inside the id-keyed slot is a new artifact and fires again, and so
 *     is a merged-row entry switching source since `surface` rides the key
 *     too), the tapped analytics, and the `{eventId}:{phase}` artifact key
 *     that remounts the pill (enter animation replays) inside its stable row
 *     slot.
 *
 * NO tick here: `now` comes from the caller's data hook (one clock per
 * surface tree, W-9). NO dismissal of any kind: pills have no ✕ (§1).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LivePill } from '@components/live';
import { SwipeRow } from '@components/ui/SwipeRow';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useEventFeedCounts } from '@hooks/useEventFeedCounts';
import { useEventHelpable } from '@hooks/useEventHelpable';
import { useHelpableSurfaceImpression } from '@hooks/useHelpableSurfaceImpression';
import type { LiveRailEntry } from '@hooks/useLiveEventsRail';
import { useLiveFrontResetKey } from '@hooks/useLiveFrontResetKey';
import { useLiveImpression } from '@hooks/useLiveImpression';
import { ANALYTICS_EVENTS, type LiveSurface } from '@lib/analytics-events';
import {
  buildEventContextPayload,
  buildEventParentSpacePayload,
  buildHelpableEventContext,
} from '@lib/analytics/payloads';
import { useAuth } from '@providers';
import { routes } from '@routes';
import { composePillGoers, composePillSegments } from './liveHomeCopy';

export interface LivePillRowProps {
  /** Already-selected entries in render order — eligibility/filtering is the caller's. */
  entries: LiveRailEntry[];
  /** The caller's surface tick (one clock per surface tree, W-9) — drives countdown copy. */
  now: Date;
  /**
   * Analytics surface stamped on every impression/tap this row fires (spec §9),
   * unless `surfaceFor` below resolves a different one for a given entry.
   */
  surface: LiveSurface;
  /**
   * Per-entry surface override for hosts that merge rails with distinct
   * funnels into one row; falls back to `surface`.
   */
  surfaceFor?: (entry: LiveRailEntry) => LiveSurface;
  /**
   * Accessible name for the row's `role="group"` (SwipeRow). Defaults to the
   * shared "Live and upcoming events" — true for every payload the row
   * renders (live + soon + upcoming; on a quiet day, entirely upcoming).
   */
  ariaLabel?: string;
}

export function LivePillRow({
  entries,
  now,
  surface,
  surfaceFor,
  ariaLabel = 'Live and upcoming events',
}: LivePillRowProps) {
  // Feed counts (T-8: one batch, never a fetch per pill): every LIVE pill's id
  // in this row, in one request. Called before the empty check below so hooks
  // stay unconditional (rules of hooks) — an empty row's batch is just empty ids.
  const liveIds = entries.filter(entry => entry.phase === 'live').map(entry => entry.event.id);
  const { getFeedCount, isCountPending } = useEventFeedCounts(liveIds);

  // Scroll-start token: this row used to forward NO `startKey`, so SwipeRow fell
  // back to the front child's own React key — the event id — and any re-rank of
  // the front pill reset the horizontal scroll under a reader's thumb.
  // `useLiveFrontResetKey` is the uniform rule (shared with `LiveSpaceSlot`):
  // reset only on an OBSERVED not-live→live transition arriving at the front. It
  // is handed the WHOLE row, not just the front, because that is the only way to
  // tell a room going live from an already-live room sliding into slot 0 as the
  // live block churns — see the hook's docblock for the full case table.
  // Unconditional, above the empty early-return.
  const startKey = useLiveFrontResetKey(
    entries.map(entry => ({ id: entry.event.id, live: entry.phase === 'live' })),
  );

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-4 mt-2">
      <SwipeRow
        ariaLabel={ariaLabel}
        depKey={entries.map(entry => `${entry.event.id}:${entry.phase}`).join('|')}
        startKey={startKey}
      >
        {entries.map(entry => {
          // A caller merging more than one source into this row (personal +
          // public) resolves each entry's OWN surface here; every other
          // caller's entries share the row's one `surface`.
          const entrySurface = surfaceFor?.(entry) ?? surface;
          return (
            <LivePillRowItem
              // Row slots keyed by event.id ALONE (branch review note): phase
              // flips and reorders must patch the slot in place, never remount
              // it — the artifact-level remount lives on the inner LivePill key.
              key={entry.event.id}
              entry={entry}
              now={now}
              surface={entrySurface}
              postsCount={entry.phase === 'live' ? (getFeedCount(entry.event.id) ?? null) : null}
              postsPending={isCountPending(entry.event.id)}
            />
          );
        })}
      </SwipeRow>
    </div>
  );
}

interface LivePillRowItemProps {
  entry: LiveRailEntry;
  now: Date;
  surface: LiveSurface;
  /** The viewer's feed count for this event (live-only; null = nothing to show). */
  postsCount: number | null;
  /** True only during the first fetch of the row's batch, per id. */
  postsPending: boolean;
}

function LivePillRowItem({ entry, now, surface, postsCount, postsPending }: LivePillRowItemProps) {
  const { event, phase } = entry;
  const navigate = useNavigate();
  const { trackEvent } = useAmplitudeTracking();

  // Helpable count for this live event (Track 2 Phase A, spec P3 + 2.3b): the
  // pill's own single event, keyed by `event.id` - the SAME identifier every
  // per-event key here already uses (feed counts, impression, enterKey), so it
  // shares the section's `eventHelpableKey(event.id)` entry (no second fetch;
  // the hook dedupes). Live Layer v2 (item 1): the chip is surface-agnostic —
  // any AUTHENTICATED viewer sees it on a LIVE pill, on BOTH the my-spaces rail
  // (`home_pill`) and the meetverse rail (`meetverse_pill`). Signed-out viewers
  // never fire `useEventHelpable`: the endpoint is viewer-scoped (auth-only), so
  // a guest's live pill carries no chip. Live-only (the endpoint is empty
  // otherwise). The hook still owns the feature-flag gate.
  const { isAuthenticated } = useAuth();
  const { total: helpableCount } = useEventHelpable(event.id, {
    enabled: phase === 'live' && isAuthenticated,
  });

  // Impression, once per APPEARANCE (event+phase; spec §9): re-renders (30s
  // ticks, refetch identity churn) are no-ops, while a phase flip — which the
  // row deliberately does NOT remount on (id-keyed slot) — is a genuine new
  // artifact and fires again (the Home rail guard). `phase` also rides the
  // PAYLOAD (Dexter's review of PR #1035): since the rows widened to upcoming,
  // a surface's impressions mix live, soon and far-future artifacts that
  // convert at very different rates, so displayed→tapped is only readable when
  // both events carry the dimension — the tap below stamps the same field.
  //
  // `surface` joins the guard key too (fresh-eyes review, finding I2): a
  // merged-row entry can switch source (personal ↔ public) inside its own
  // id-keyed slot without remounting, so each funnel's `displayed` leg must
  // fire under its new surface — a no-op for constant-surface callers.
  useLiveImpression(surface, event, { phase, appearanceKey: `${surface}:${phase}` });

  // `pill_help_chip` impression (Track 2 Phase A, spec 3.5): once per appearance
  // WHEN the tinted count chip is actually visible (count > 0), alongside the
  // pill's own impression above. The count resolves after mount, so this fires
  // on the render it becomes positive, never before. Keyed by event+phase like
  // the pill impression, so a soon→live flip in the id-keyed slot re-arms it.
  const helpableContext = useMemo(() => buildHelpableEventContext(event), [event]);
  useHelpableSurfaceImpression({
    surface: 'pill_help_chip',
    eventContext: helpableContext,
    helpableCount,
    when: helpableCount > 0,
    appearanceKey: `${event.id}:${phase}`,
  });

  return (
    <LivePill
      // Artifact identity: a phase flip remounts the pill itself (enter
      // animation replays for the genuinely new appearance) inside its stable
      // row slot.
      key={`${event.id}:${phase}`}
      segments={composePillSegments(event, phase, now, postsCount, postsPending)}
      goers={composePillGoers(event, phase)}
      // Non-live pills (soon AND upcoming) wear the static dot; only a live
      // pill pulses. `composePillGoers` already returns null off-live, so the
      // faces branch stays live-only regardless.
      soon={phase !== 'live'}
      helpableCount={helpableCount}
      enterKey={`${event.id}:${phase}`}
      // The §0 width rule at EVERY count (#967 review, Dexter's ruling:
      // "exactly 1 item → 100% width"): SwipeRow owns the slot widths (multi =
      // 90% peek slots, single = full width); a ROW pill always fills its
      // slot. LivePill's default w-fit content hug is for standalone callers,
      // never for row pills — gating w-full on multi-ness made a lone pill
      // content-hug.
      className="w-full"
      onTap={() => {
        trackEvent(ANALYTICS_EVENTS.live_surface_tapped, {
          surface,
          // Same dimension as the impression above — a tap rate per phase is
          // only computable when both halves of the funnel carry it.
          phase,
          ...buildEventContextPayload(event),
          ...buildEventParentSpacePayload(event),
        });
        navigate(routes.event.details(event, { returnTo: routes.home() }));
      }}
    />
  );
}
